/// <reference types="google.maps" />
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";
import { Anchor, Content, Portal, Root } from "@radix-ui/react-popover";
import debounce from "lodash.debounce";
import { Check, ChevronsUpDown, Loader2, Search } from "lucide-react";
import { ensureGoogleMapsPlacesLoaded } from "utils/googleMapsLoader";
import { cn } from "utils/cn";

const MIN_QUERY_LENGTH = 2;

export interface GoogleMapsLocationValue {
  name: string;
  place_id: string | null;
}

export interface GoogleMapsAutocompleteInputProps {
  name: string;
  placeholder: string;
  value: GoogleMapsLocationValue;
  onChange: (value: GoogleMapsLocationValue) => void;
  disabled?: boolean;
  /** Called when the user selects a Places suggestion (runs after `onChange`). */
  onPlacePredictionSelected?: (pick: {
    mainText: string;
    fullText: string;
    placeId: string;
  }) => void;
  className?: string;
  /** When false, hides the inline API-unavailable warning (parent may explain instead). */
  showUnavailableHint?: boolean;
}

type PlacePredictionRow = {
  placeId: string;
  label: string;
  mainText: string;
};

function getPlacesLibrary(): Promise<google.maps.PlacesLibrary> {
  if (typeof window === "undefined" || !window.google?.maps?.importLibrary) {
    return Promise.reject(new Error("Google Maps JS API is not loaded"));
  }
  return window.google.maps.importLibrary("places") as Promise<google.maps.PlacesLibrary>;
}

function predictionToRow(prediction: google.maps.places.PlacePrediction): PlacePredictionRow {
  const mainText = prediction.mainText?.text ?? prediction.text.text;
  return {
    placeId: prediction.placeId,
    label: prediction.text.text,
    mainText,
  };
}

const inputShellClass = cn(
  "flex h-10 w-full min-w-0 items-stretch rounded-md border border-border bg-muted text-sm text-foreground outline-none transition",
  "focus-within:border-primary"
);

const inputFieldClass = cn(
  "min-w-0 flex-1 border-0 bg-transparent py-2 pl-9 pr-2 outline-none placeholder:text-muted-foreground",
  "disabled:cursor-not-allowed disabled:opacity-50"
);

export const GoogleMapsAutocompleteInput = ({
  name,
  placeholder,
  value,
  onChange,
  disabled = false,
  onPlacePredictionSelected,
  className,
  showUnavailableHint = true,
}: GoogleMapsAutocompleteInputProps) => {
  const listboxId = useId();
  const anchorRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const placesLibRef = useRef<google.maps.PlacesLibrary | null>(null);
  const sessionTokenRef = useRef<google.maps.places.AutocompleteSessionToken | null>(null);
  const requestIdRef = useRef(0);

  /** "loading" until the Places library resolves; only "unavailable" means the load failed. */
  const [placesStatus, setPlacesStatus] = useState<"loading" | "ready" | "unavailable">("loading");
  const placesReady = placesStatus === "ready";
  const placesLoading = placesStatus === "loading";
  /** Latest typed text, so the search can run on it once Places becomes ready. */
  const valueNameRef = useRef(value.name);
  valueNameRef.current = value.name;
  /**
   * True when the user typed text that no search has run for yet (typed while Places was
   * loading). A saved or picked value stays false, so focusing it does not search again.
   */
  const hasUnsearchedTextRef = useRef(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(false);
  const [suggestions, setSuggestions] = useState<PlacePredictionRow[]>([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [contentWidth, setContentWidth] = useState<number | undefined>(undefined);

  const startNewSession = useCallback(() => {
    const lib = placesLibRef.current;
    if (!lib) return;
    sessionTokenRef.current = new lib.AutocompleteSessionToken();
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await ensureGoogleMapsPlacesLoaded();
        const lib = await getPlacesLibrary();
        if (cancelled) return;
        placesLibRef.current = lib;
        sessionTokenRef.current = new lib.AutocompleteSessionToken();
        setPlacesStatus("ready");
      } catch {
        if (!cancelled) {
          placesLibRef.current = null;
          sessionTokenRef.current = null;
          setPlacesStatus("unavailable");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const executeFetch = useCallback(async (input: string) => {
    const lib = placesLibRef.current;
    const sessionToken = sessionTokenRef.current;
    if (!lib || !sessionToken || input.length < MIN_QUERY_LENGTH) {
      setSuggestions([]);
      setLoading(false);
      setFetchError(false);
      return;
    }

    hasUnsearchedTextRef.current = false;
    const myRequest = ++requestIdRef.current;
    setLoading(true);
    setFetchError(false);

    try {
      const { suggestions: raw } = await lib.AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input,
        sessionToken,
      });

      if (myRequest !== requestIdRef.current) return;

      const rows: PlacePredictionRow[] = [];
      for (const s of raw) {
        if (s.placePrediction) {
          rows.push(predictionToRow(s.placePrediction));
        }
      }
      setSuggestions(rows);
      setHighlightedIndex(rows.length > 0 ? 0 : -1);
    } catch {
      if (myRequest !== requestIdRef.current) return;
      setSuggestions([]);
      setFetchError(true);
      setHighlightedIndex(-1);
    } finally {
      if (myRequest === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, []);

  const debouncedFetch = useMemo(() => debounce(executeFetch, 350), [executeFetch]);

  useEffect(
    () => () => {
      debouncedFetch.cancel();
    },
    [debouncedFetch]
  );

  const measureWidth = useCallback(() => {
    const w = anchorRef.current?.offsetWidth;
    if (w) setContentWidth(w);
  }, []);

  // Text typed while Places was loading could not be searched; search it once Places is ready.
  useEffect(() => {
    if (!placesReady) return;
    const trimmed = valueNameRef.current.trim();
    const focused = typeof document !== "undefined" && document.activeElement === inputRef.current;
    if (!focused || trimmed.length < MIN_QUERY_LENGTH) return;
    setPanelOpen(true);
    setLoading(true);
    measureWidth();
    debouncedFetch(trimmed);
  }, [placesReady, debouncedFetch, measureWidth]);

  const handleInputChange = (raw: string) => {
    onChange({ name: raw, place_id: null });
    setFetchError(false);
    const trimmed = raw.trim();

    hasUnsearchedTextRef.current = trimmed.length >= MIN_QUERY_LENGTH;
    if (trimmed.length < MIN_QUERY_LENGTH) {
      debouncedFetch.cancel();
      setSuggestions([]);
      setLoading(false);
      setPanelOpen(false);
      setHighlightedIndex(-1);
      return;
    }

    if (placesReady) {
      setPanelOpen(true);
      setLoading(true);
    }
    measureWidth();
    debouncedFetch(trimmed);
  };

  const applySelection = (row: PlacePredictionRow) => {
    debouncedFetch.cancel();
    onChange({ name: row.label, place_id: row.placeId });
    onPlacePredictionSelected?.({
      mainText: row.mainText,
      fullText: row.label,
      placeId: row.placeId,
    });
    setSuggestions([]);
    setPanelOpen(false);
    setHighlightedIndex(-1);
    setLoading(false);
    setFetchError(false);
    startNewSession();
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    const canNavigate = panelOpen && suggestions.length > 0;

    if (e.key === "ArrowDown") {
      if (!canNavigate) return;
      e.preventDefault();
      setHighlightedIndex((i) => (i < suggestions.length - 1 ? i + 1 : 0));
    } else if (e.key === "ArrowUp") {
      if (!canNavigate) return;
      e.preventDefault();
      setHighlightedIndex((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
    } else if (e.key === "Enter") {
      if (!canNavigate) return;
      e.preventDefault();
      const row = suggestions[highlightedIndex >= 0 ? highlightedIndex : 0];
      if (row) applySelection(row);
    } else if (e.key === "Escape") {
      if (panelOpen) {
        e.preventDefault();
        setPanelOpen(false);
      }
    }
  };

  const showPanel =
    panelOpen && placesReady && !disabled && value.name.trim().length >= MIN_QUERY_LENGTH;

  const showEmptyHint =
    !loading &&
    !fetchError &&
    suggestions.length === 0 &&
    value.name.trim().length >= MIN_QUERY_LENGTH;

  const notReadyMessage = placesStatus === "unavailable" && !disabled;
  const showPlacesLoadingCue =
    placesLoading && !disabled && value.name.trim().length >= MIN_QUERY_LENGTH;

  return (
    <Root
      open={showPanel}
      onOpenChange={(next) => {
        if (!next) setPanelOpen(false);
      }}
      modal={false}
    >
      <div className={cn("relative w-full", className)}>
        <Anchor asChild>
          <div ref={anchorRef} className="relative">
            <div className={cn(inputShellClass, disabled && "cursor-not-allowed opacity-60")}>
              <Search
                className="pointer-events-none absolute left-3 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                aria-hidden
              />
              <input
                ref={inputRef}
                id={name}
                name={name}
                type="search"
                autoComplete="off"
                disabled={disabled}
                value={value.name}
                placeholder={placeholder}
                role="combobox"
                aria-expanded={showPanel}
                aria-controls={listboxId}
                aria-autocomplete="list"
                aria-busy={loading || placesLoading}
                onChange={(e) => handleInputChange(e.target.value)}
                onFocus={() => {
                  measureWidth();
                  const trimmed = value.name.trim();
                  if (trimmed.length >= MIN_QUERY_LENGTH && placesReady) {
                    setPanelOpen(true);
                    // Text typed during the load, then left, was never searched.
                    if (hasUnsearchedTextRef.current) {
                      setLoading(true);
                      debouncedFetch(trimmed);
                    }
                  }
                }}
                onBlur={() => {
                  window.setTimeout(() => setPanelOpen(false), 150);
                }}
                onKeyDown={handleKeyDown}
                className={inputFieldClass}
              />
              <button
                type="button"
                tabIndex={-1}
                disabled={disabled}
                aria-label="Show place suggestions"
                aria-expanded={showPanel}
                className="shrink-0 border-l border-border px-2 text-muted-foreground hover:text-muted-foreground disabled:pointer-events-none"
                onMouseDown={(e) => {
                  e.preventDefault();
                  measureWidth();
                  if (!placesReady || disabled) return;
                  if (value.name.trim().length >= MIN_QUERY_LENGTH) {
                    setPanelOpen((open) => !open);
                  }
                }}
              >
                {loading || showPlacesLoadingCue ? (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                ) : (
                  <ChevronsUpDown className="h-4 w-4" aria-hidden />
                )}
              </button>
            </div>
          </div>
        </Anchor>

        {notReadyMessage && showUnavailableHint ? (
          <p className="mt-1 text-xs text-muted-foreground" role="status">
            Location search is unavailable.
          </p>
        ) : null}

        {showPlacesLoadingCue ? (
          <p className="mt-1 text-xs text-muted-foreground" role="status">
            Loading location search…
          </p>
        ) : null}

        <Portal>
          <Content
            data-google-maps-place-suggestions=""
            side="bottom"
            align="start"
            sideOffset={4}
            collisionPadding={8}
            style={contentWidth ? { width: contentWidth } : undefined}
            className={cn(
              "z-[9999] max-h-60 overflow-y-auto rounded-lg border border-border",
              "bg-card p-1 shadow-lg"
            )}
            onOpenAutoFocus={(ev) => ev.preventDefault()}
            onWheel={(ev) => ev.stopPropagation()}
          >
            <div
              id={listboxId}
              role="listbox"
              aria-label="Place suggestions"
              className="flex flex-col gap-0.5"
            >
              {loading ? (
                <div className="px-2.5 py-2 text-sm text-muted-foreground">Searching…</div>
              ) : null}
              {fetchError ? (
                <div className="px-2.5 py-2 text-sm text-danger">Could not load suggestions.</div>
              ) : null}
              {showEmptyHint && !loading ? (
                <div className="px-2.5 py-2 text-sm text-muted-foreground">No matching places.</div>
              ) : null}
              {!loading &&
                !fetchError &&
                suggestions.map((row, index) => {
                  const active = index === highlightedIndex;
                  const selected = value.place_id === row.placeId && value.name === row.label;
                  return (
                    <button
                      key={row.placeId}
                      type="button"
                      role="option"
                      aria-selected={selected}
                      className={cn(
                        "flex w-full items-center gap-2 rounded-sm px-2.5 py-1.5 text-left text-sm text-foreground",
                        active ? "bg-muted" : "hover:bg-muted"
                      )}
                      onMouseEnter={() => setHighlightedIndex(index)}
                      onMouseDown={(ev) => {
                        ev.preventDefault();
                        applySelection(row);
                      }}
                    >
                      <span className="min-w-0 flex-1 truncate">{row.label}</span>
                      {selected ? (
                        <Check className="h-4 w-4 shrink-0 text-primary" aria-hidden />
                      ) : null}
                    </button>
                  );
                })}
            </div>
          </Content>
        </Portal>
      </div>
    </Root>
  );
};
